import { STRINGS } from '@tm/shared'
import { createModeToggle, createIconButton } from '@tm/ui-kit'
import { trackEvent } from '../analytics/plausible.js'

const LONG_PRESS_MS = 600
const SAVE_STATUS_LABELS = {
  idle: STRINGS.headerSaveStatusIdle,
  dirty: STRINGS.headerSaveStatusDirty,
  saving: STRINGS.headerSaveStatusSaving,
  saved: STRINGS.headerSaveStatusSaved,
  error: STRINGS.headerSaveStatusError,
}

export function mountHeader(
  rootEl,
  {
    activeMode,
    onModeChange,
    onSave,
    onLongPressSave,
    onProjects,
    onNew,
    onShare,
    onHelp,
    initialSaveStatus = 'idle',
  },
) {
  rootEl.innerHTML = `
    <header class="tm-header">
      <div class="tm-header__logo">🎮 ${STRINGS.appTitle}</div>
      <div id="tm-mode-toggle" role="tablist"></div>
      <div class="tm-header__actions" id="tm-header-actions"></div>
    </header>
  `

  const toggleHost = rootEl.querySelector('#tm-mode-toggle')
  const toggle = createModeToggle({
    active: activeMode,
    onChange: (mode) => {
      trackEvent('mode_switched', { mode })
      onModeChange(mode)
    },
  })
  toggleHost.appendChild(toggle)

  const actionsHost = rootEl.querySelector('#tm-header-actions')

  // Save button — status indicator + click (immediate save) + long-press (manual save fallback).
  const saveBtn = document.createElement('button')
  saveBtn.className = 'tm-icon-btn tm-header__save'
  saveBtn.type = 'button'
  saveBtn.setAttribute('aria-label', 'Simpan proyek')
  saveBtn.dataset.tmAction = 'save'
  saveBtn.dataset.tmSaveStatus = initialSaveStatus
  saveBtn.textContent = SAVE_STATUS_LABELS[initialSaveStatus] || SAVE_STATUS_LABELS.idle

  let pressTimer = null
  let longPressFired = false

  function clearPressTimer() {
    if (pressTimer !== null) {
      clearTimeout(pressTimer)
      pressTimer = null
    }
  }

  saveBtn.addEventListener('click', () => {
    // Click setelah long-press akan di-suppress (longPressFired = true).
    if (longPressFired) {
      longPressFired = false
      return
    }
    trackEvent('action_clicked', { action: 'save' })
    onSave?.()
  })

  saveBtn.addEventListener('pointerdown', () => {
    longPressFired = false
    clearPressTimer()
    pressTimer = setTimeout(() => {
      pressTimer = null
      longPressFired = true
      trackEvent('action_clicked', { action: 'save_long_press' })
      onLongPressSave?.()
    }, LONG_PRESS_MS)
  })

  saveBtn.addEventListener('pointerup', clearPressTimer)
  saveBtn.addEventListener('pointerleave', clearPressTimer)
  saveBtn.addEventListener('pointercancel', clearPressTimer)

  actionsHost.appendChild(saveBtn)

  // Share button — opens ShareModal via App.js handleShare (confirm opt-in di sana).
  if (typeof onShare === 'function') {
    actionsHost.appendChild(
      createIconButton({
        icon: '🔗',
        label: STRINGS.headerShare,
        ariaLabel: 'Bagikan proyek',
        onClick: () => {
          trackEvent('action_clicked', { action: 'share' })
          onShare?.()
        },
      }),
    )
  }

  // Help button — buka ParentGuide modal (F-07 M7).
  if (typeof onHelp === 'function') {
    actionsHost.appendChild(
      createIconButton({
        icon: '❓',
        label: STRINGS.headerHelp,
        ariaLabel: STRINGS.parentGuideOpenLabel,
        dataset: { tmAction: 'help' },
        onClick: () => {
          trackEvent('action_clicked', { action: 'help' })
          onHelp?.()
        },
      }),
    )
  }

  actionsHost.appendChild(
    createIconButton({
      icon: '📂',
      label: STRINGS.headerProjects,
      ariaLabel: 'Buka proyek',
      onClick: () => {
        trackEvent('action_clicked', { action: 'projects' })
        onProjects?.()
      },
    }),
  )
  actionsHost.appendChild(
    createIconButton({
      icon: '🆕',
      label: STRINGS.headerNew,
      ariaLabel: 'Proyek baru',
      onClick: () => {
        trackEvent('action_clicked', { action: 'new' })
        onNew?.()
      },
    }),
  )

  function renderSaveStatus(status) {
    if (!status) return
    saveBtn.dataset.tmSaveStatus = status
    saveBtn.textContent = SAVE_STATUS_LABELS[status] || SAVE_STATUS_LABELS.idle
  }

  return { renderSaveStatus, saveButton: saveBtn }
}
